import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { clamp } from '../utils';

// Extended device presets
export const DEVICE_PRESETS = {
  // Desktop
  'desktop': { name: 'Desktop', width: 1280, height: 720 },
  'desktop-1080': { name: 'Desktop 1080p', width: 1920, height: 1080 },
  'desktop-1440': { name: 'Desktop 1440p', width: 2560, height: 1440 },
  // Apple Devices
  'iphone-15': { name: 'iPhone 15', width: 393, height: 852 },
  'iphone-15-plus': { name: 'iPhone 15 Plus', width: 430, height: 932 },
  'iphone-15-pro-max': { name: 'iPhone 15 Pro Max', width: 430, height: 932 },
  'iphone-se': { name: 'iPhone SE', width: 375, height: 667 },
  'ipad-pro-12': { name: 'iPad Pro 12.9"', width: 1024, height: 1366 },
  'ipad-pro-11': { name: 'iPad Pro 11"', width: 834, height: 1194 },
  'ipad-air': { name: 'iPad Air', width: 820, height: 1180 },
  'ipad-mini': { name: 'iPad Mini', width: 768, height: 1024 },
  // Android Devices
  'pixel-8': { name: 'Pixel 8', width: 412, height: 915 },
  'pixel-8-pro': { name: 'Pixel 8 Pro', width: 448, height: 998 },
  'galaxy-s24': { name: 'Galaxy S24', width: 384, height: 824 },
  'galaxy-s24-ultra': { name: 'Galaxy S24 Ultra', width: 412, height: 915 },
  'galaxy-fold': { name: 'Galaxy Fold', width: 280, height: 653 },
  // Tablets
  'tablet': { name: 'Tablet', width: 900, height: 700 },
  // Legacy
  'mobile': { name: 'Mobile', width: 430, height: 900 },
  // Custom
  'custom': { name: 'Custom', width: 0, height: 0 },
} as const;

export type PreviewDevice = keyof typeof DEVICE_PRESETS;
export type PreviewOrientation = 'portrait' | 'landscape';

type PreviewContextValue = {
  hotReloadEnabled: boolean;
  setHotReloadEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  toggleHotReload: () => void;
  previewVersion: number;
  setPreviewVersion: React.Dispatch<React.SetStateAction<number>>;
  restartPreview: () => void;
  sandboxedPreview: boolean;
  setSandboxedPreview: React.Dispatch<React.SetStateAction<boolean>>;
  toggleSandbox: () => void;
  previewDevice: PreviewDevice;
  setPreviewDevice: React.Dispatch<React.SetStateAction<PreviewDevice>>;
  previewOrientation: PreviewOrientation;
  setPreviewOrientation: React.Dispatch<React.SetStateAction<PreviewOrientation>>;
  toggleOrientation: () => void;
  customPreviewWidth: number;
  setCustomPreviewWidth: React.Dispatch<React.SetStateAction<number>>;
  setCustomPreviewWidthClamped: (width: number) => void;
  touchMode: boolean;
  setTouchMode: React.Dispatch<React.SetStateAction<boolean>>;
  toggleTouchMode: () => void;
  showDeviceFrame: boolean;
  setShowDeviceFrame: React.Dispatch<React.SetStateAction<boolean>>;
  toggleDeviceFrame: () => void;
};

const PreviewContext = createContext<PreviewContextValue | null>(null);

export function PreviewProvider({ children }: { children: React.ReactNode }) {
  const [hotReloadEnabled, setHotReloadEnabled] = useState(true);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [sandboxedPreview, setSandboxedPreview] = useState(true);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');
  const [previewOrientation, setPreviewOrientation] = useState<PreviewOrientation>('portrait');
  const [customPreviewWidth, setCustomPreviewWidth] = useState(1100);
  const [touchMode, setTouchMode] = useState(false);
  const [showDeviceFrame, setShowDeviceFrame] = useState(false);

  const toggleHotReload = useCallback(() => {
    setHotReloadEnabled((v) => !v);
    setPreviewVersion((v) => v + 1);
  }, []);

  const restartPreview = useCallback(() => {
    setPreviewVersion((v) => v + 1);
  }, []);

  const toggleSandbox = useCallback(() => {
    setSandboxedPreview((v) => !v);
  }, []);

  const toggleOrientation = useCallback(() => {
    setPreviewOrientation((v) => (v === 'portrait' ? 'landscape' : 'portrait'));
  }, []);

  const setCustomPreviewWidthClamped = useCallback((width: number) => {
    setCustomPreviewWidth(clamp(width, 320, 1600));
  }, []);

  const toggleTouchMode = useCallback(() => {
    setTouchMode((v) => !v);
  }, []);

  const toggleDeviceFrame = useCallback(() => {
    setShowDeviceFrame((v) => !v);
  }, []);

  const value = useMemo<PreviewContextValue>(
    () => ({
      hotReloadEnabled,
      setHotReloadEnabled,
      toggleHotReload,
      previewVersion,
      setPreviewVersion,
      restartPreview,
      sandboxedPreview,
      setSandboxedPreview,
      toggleSandbox,
      previewDevice,
      setPreviewDevice,
      previewOrientation,
      setPreviewOrientation,
      toggleOrientation,
      customPreviewWidth,
      setCustomPreviewWidth,
      setCustomPreviewWidthClamped,
      touchMode,
      setTouchMode,
      toggleTouchMode,
      showDeviceFrame,
      setShowDeviceFrame,
      toggleDeviceFrame,
    }),
    [
      hotReloadEnabled,
      toggleHotReload,
      previewVersion,
      restartPreview,
      sandboxedPreview,
      toggleSandbox,
      previewDevice,
      previewOrientation,
      toggleOrientation,
      customPreviewWidth,
      setCustomPreviewWidthClamped,
      touchMode,
      toggleTouchMode,
      showDeviceFrame,
      toggleDeviceFrame,
    ]
  );

  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
}

export function usePreviewContext() {
  const context = useContext(PreviewContext);
  if (!context) {
    throw new Error('usePreviewContext must be used within a PreviewProvider');
  }
  return context;
}
